import { useRef, useState } from 'react';
import { STATUSES, PRIORITIES } from '../../data/constants';
import { Badge, Button, Modal, Input, Textarea, Select } from '../ui';

const detailLabel = {
  fontSize: 11,
  fontWeight: 700,
  color: '#94a3b8',
  letterSpacing: '0.08em',
  marginBottom: 6,
};

/* ── ViewTaskModal ───────────────────────────────────────── */
export function ViewTaskModal({
  task,
  projectColor = '#6C63FF',
  projectTitle,
  rootProjectTitle,
  isSubproject,
  onClose,
  onEdit,
  onDelete,
  onOpenProject,
}) {
  const statusMeta = STATUSES.find(s => s.id === task.status) ?? STATUSES[0];
  const priorityMeta = PRIORITIES[task.priority] ?? PRIORITIES.medium;

  const projectLabel = projectTitle
    ? (isSubproject && rootProjectTitle ? `${rootProjectTitle} → ${projectTitle}` : projectTitle)
    : null;

  return (
    <Modal title="Task Details" onClose={onClose} maxWidth={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div>
          <p style={detailLabel}>TITLE</p>
          <h3 style={{
            fontSize: 20, fontWeight: 800, color: '#0f0f1a',
            letterSpacing: '-0.02em', lineHeight: 1.35,
          }}>
            {task.title}
          </h3>
        </div>

        <div>
          <p style={detailLabel}>DESCRIPTION</p>
          <p style={{
            fontSize: 14, color: task.desc ? '#475569' : '#94a3b8',
            lineHeight: 1.6, whiteSpace: 'pre-wrap',
          }}>
            {task.desc || 'No description provided.'}
          </p>
        </div>

        <div>
          <p style={detailLabel}>STATUS & PRIORITY</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Badge label={statusMeta.label} color={statusMeta.color} bg={statusMeta.bg} />
            <Badge label={priorityMeta.label} color={priorityMeta.color} bg={priorityMeta.bg} />
          </div>
        </div>

        {projectLabel && (
          <div>
            <p style={detailLabel}>PROJECT</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: 14, fontWeight: 600, color: projectColor,
              }}>
                <span style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: projectColor, flexShrink: 0,
                }} />
                {projectLabel}
              </span>
              {onOpenProject && (
                <button
                  type="button"
                  onClick={onOpenProject}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    fontSize: 13, fontWeight: 600, color: projectColor,
                    cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  Open project
                </button>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {onDelete && (
            <Button variant="danger" onClick={onDelete}>Delete</Button>
          )}
          <Button
            variant="primary"
            onClick={onEdit}
            style={{ background: projectColor, boxShadow: `0 2px 8px ${projectColor}44` }}
          >
            Edit task
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ── EditTaskModal ───────────────────────────────────────── */
export function EditTaskModal({ task, projectColor, onClose, onSave }) {
  const [title, setTitle] = useState(task.title);
  const [desc, setDesc] = useState(task.desc || '');
  const [status, setStatus] = useState(task.status);
  const [priority, setPriority] = useState(task.priority);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Task title is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        desc: desc.trim(),
        status,
        priority,
      });
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not save task.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Edit Task" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <Input
          label="Task title"
          required
          value={title}
          onChange={e => { setTitle(e.target.value); setError(''); }}
          placeholder="What needs to be done?"
        />
        <Textarea
          label="Description"
          value={desc}
          onChange={e => setDesc(e.target.value)}
          placeholder="Additional details…"
          rows={2}
        />
        <div className="form-grid-2">
          <Select
            label="Status"
            value={status}
            onChange={e => setStatus(e.target.value)}
            options={STATUSES.map(s => ({ value: s.id, label: s.label }))}
          />
          <Select
            label="Priority"
            value={priority}
            onChange={e => setPriority(e.target.value)}
            options={Object.entries(PRIORITIES).map(([k, v]) => ({ value: k, label: v.label }))}
          />
        </div>
        {error && (
          <p style={{ fontSize: 13, color: '#ef4444', background: '#fee2e2', padding: '8px 12px', borderRadius: 8 }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            style={{ background: projectColor, boxShadow: `0 2px 8px ${projectColor}44` }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ── TaskCard ──────────────────────────────────────────── */
function TaskCard({ task, onUpdate, onView, onEdit, onDelete, onDragStart, onDragEnd, isDragging }) {
  const [showMenu, setShowMenu] = useState(false);
  const priority = PRIORITIES[task.priority];

  const handleCardClick = (e) => {
    if (e.target.closest('button')) return;
    onView();
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onView(); } }}
      style={{
        background: '#fff', borderRadius: 12, padding: '0.875rem',
        border: isDragging ? '1.5px dashed #6C63FF' : '1.5px solid #f1f5f9',
        opacity: isDragging ? 0.45 : 1,
        boxShadow: isDragging ? '0 12px 32px rgba(108,99,255,0.2)' : '0 1px 3px rgba(0,0,0,0.04)',
        cursor: 'pointer', userSelect: 'none',
        transition: 'opacity 0.15s, box-shadow 0.15s',
        position: 'relative',
      }}
      onMouseEnter={e => { if (!isDragging) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { if (!isDragging) e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
    >
      {/* Drag handle hint */}
      <div style={{
        position: 'absolute', top: 10, right: 10,
        display: 'flex', flexDirection: 'column', gap: 3, opacity: 0.25,
      }}>
        {[0,1,2].map(i => <div key={i} style={{ display: 'flex', gap: 3 }}>
          {[0,1].map(j => <div key={j} style={{ width: 3, height: 3, borderRadius: '50%', background: '#64748b' }} />)}
        </div>)}
      </div>

      <p style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.4, paddingRight: 20, marginBottom: task.desc ? 6 : 10 }}>
        {task.title}
      </p>
      {task.desc && (
        <p style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5, marginBottom: 10 }}>{task.desc}</p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Badge label={priority.label} color={priority.color} bg={priority.bg} />

        {/* Priority picker */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            style={{
              width: 24, height: 24, borderRadius: 6, border: '1.5px solid #e2e8f0',
              background: '#f8fafc', color: '#94a3b8', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >⋯</button>
          {showMenu && (
            <div style={{
              position: 'absolute', right: 0, top: 30, background: '#fff',
              border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 20, minWidth: 150,
            }}>
              <button
                type="button"
                onClick={() => { onEdit(); setShowMenu(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '8px 12px', border: 'none', background: '#fff',
                  fontSize: 13, color: '#374151', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit task
              </button>
              <div style={{ borderTop: '1px solid #f1f5f9' }} />
              <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', padding: '8px 12px 4px' }}>PRIORITY</p>
              {Object.entries(PRIORITIES).map(([key, val]) => (
                <button key={key} onClick={() => { onUpdate({ priority: key }); setShowMenu(false); }} style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '7px 12px', border: 'none', background: task.priority === key ? '#f8fafc' : '#fff',
                  fontSize: 13, color: '#374151', cursor: 'pointer',
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: val.color }} />
                  {val.label}
                </button>
              ))}
              <div style={{ borderTop: '1px solid #f1f5f9' }}>
                <button onClick={() => { onDelete(); setShowMenu(false); }} style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '7px 12px', border: 'none', background: '#fff',
                  fontSize: 13, color: '#ef4444', cursor: 'pointer',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>
                  Delete task
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Column ────────────────────────────────────────────── */
function Column({ col, tasks, draggingId, onDrop, onDragOver, onDragLeave, isOver, onTaskUpdate, onTaskView, onTaskEdit, onTaskDelete, onDragStart, onDragEnd, onAddTask }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');

  const handleAdd = () => {
    if (!title.trim()) return;
    onAddTask({ title: title.trim(), status: col.id, priority: 'medium', desc: '' });
    setTitle('');
    setAdding(false);
  };

  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
      style={{
        background: isOver ? col.bg : '#f8fafc',
        borderRadius: 16, padding: '1rem',
        border: `2px ${isOver ? 'dashed' : 'solid'} ${isOver ? col.color : '#f1f5f9'}`,
        transition: 'all 0.15s', minHeight: 200,
        display: 'flex', flexDirection: 'column', gap: 0,
      }}
    >
      {/* Column header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: col.color }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#374151', letterSpacing: '0.01em' }}>{col.label}</span>
          <span style={{
            minWidth: 22, height: 22, borderRadius: 22, background: col.color + '20',
            color: col.color, fontSize: 12, fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px',
          }}>{tasks.length}</span>
        </div>
        <button
          onClick={() => setAdding(true)}
          title="Add task"
          style={{
            width: 26, height: 26, borderRadius: 8, border: '1.5px solid #e2e8f0',
            background: '#fff', color: '#94a3b8', fontSize: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = col.color; e.currentTarget.style.color = col.color; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#94a3b8'; }}
        >+</button>
      </div>

      {/* Tasks */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        {tasks.map(task => (
          <TaskCard
            key={task.id}
            task={task}
            isDragging={draggingId === task.id}
            onDragStart={() => onDragStart(task.id)}
            onDragEnd={onDragEnd}
            onUpdate={changes => onTaskUpdate(task.id, changes)}
            onView={() => onTaskView(task)}
            onEdit={() => onTaskEdit(task)}
            onDelete={() => onTaskDelete(task.id)}
          />
        ))}

        {/* Drop indicator */}
        {isOver && (
          <div style={{
            border: `2px dashed ${col.color}`, borderRadius: 12, height: 60,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: col.color, fontSize: 13, fontWeight: 600, opacity: 0.7,
          }}>
            Drop here
          </div>
        )}
      </div>

      {/* Inline add form */}
      {adding && (
        <div style={{
          marginTop: 8, background: '#fff', borderRadius: 12, padding: '0.75rem',
          border: `1.5px solid ${col.color}55`,
        }}>
          <input
            autoFocus
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setAdding(false); setTitle(''); } }}
            placeholder="Task title… (Enter to save)"
            style={{
              width: '100%', border: 'none', outline: 'none',
              fontSize: 14, color: '#1e293b', background: 'transparent', lineHeight: 1.5,
            }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button onClick={handleAdd} style={{
              padding: '5px 14px', borderRadius: 8, background: col.color,
              color: '#fff', border: 'none', fontSize: 12, fontWeight: 600,
            }}>Add</button>
            <button onClick={() => { setAdding(false); setTitle(''); }} style={{
              padding: '5px 12px', borderRadius: 8, background: '#f1f5f9',
              color: '#64748b', border: 'none', fontSize: 12,
            }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── KanbanBoard ───────────────────────────────────────── */
export default function KanbanBoard({ tasks, onTaskUpdate, onTaskDelete, onAddTask, projectColor = '#6C63FF' }) {
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const lastDraggedRef = useRef(null);

  const handleDragStart = (id) => {
    setDraggingId(id);
  };

  const handleDragEnd = () => {
    if (draggingId) lastDraggedRef.current = draggingId;
    setDraggingId(null);
    setDragOverCol(null);
    window.setTimeout(() => { lastDraggedRef.current = null; }, 250);
  };

  const handleTaskView = (task) => {
    if (lastDraggedRef.current === task.id) return;
    setViewingTask(task);
  };

  const handleDrop = (colId) => {
    if (draggingId) {
      const task = tasks.find(t => t.id === draggingId);
      if (task && task.status !== colId) {
        onTaskUpdate(draggingId, { status: colId });
      }
    }
    setDraggingId(null);
    setDragOverCol(null);
  };

  return (
    <>
      <div className="kanban-board">
        {STATUSES.map(col => (
          <Column
            key={col.id}
            col={col}
            tasks={tasks.filter(t => t.status === col.id)}
            draggingId={draggingId}
            isOver={dragOverCol === col.id}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.id); }}
            onDrop={() => handleDrop(col.id)}
            onDragLeave={() => setDragOverCol(null)}
            onTaskUpdate={onTaskUpdate}
            onTaskView={handleTaskView}
            onTaskEdit={setEditingTask}
            onTaskDelete={onTaskDelete}
            onAddTask={onAddTask}
          />
        ))}
      </div>

      {viewingTask && (
        <ViewTaskModal
          task={viewingTask}
          projectColor={projectColor}
          onClose={() => setViewingTask(null)}
          onEdit={() => {
            setEditingTask(viewingTask);
            setViewingTask(null);
          }}
          onDelete={() => {
            onTaskDelete(viewingTask.id);
            setViewingTask(null);
          }}
        />
      )}

      {editingTask && (
        <EditTaskModal
          task={editingTask}
          projectColor={projectColor}
          onClose={() => setEditingTask(null)}
          onSave={changes => onTaskUpdate(editingTask.id, changes)}
        />
      )}
    </>
  );
}
